import React from 'react';
import styled, { keyframes } from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import SeoHead from '../components/SeoHead';
import { formatTrialCreditUsd, ORGANIC_TRIAL_DAYS } from '../config/trialTerms';

// ============================================================================
// Animations & Layout Base
// ============================================================================

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to   { opacity: 1; transform: translateY(0); }
`;


const Page = styled.main`
  background: var(--brand-canvas);
  min-height: 100vh;
  padding: 0 0 3rem;
  overflow-x: hidden;
  width: 100%;
`;

const TwoColumnLayout = styled.div`
  max-width: 1300px;
  margin: 0 auto;
  padding: 8rem 2rem 6rem;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 5rem;
  align-items: center;

  @media (max-width: 960px) {
    grid-template-columns: 1fr;
    padding: 6rem 1.25rem 4rem;
    gap: 3rem;
  }
`;

const Hero = styled.section`
  animation: ${fadeIn} 0.45s ease both;
`;

const Eyebrow = styled.p`
  font-size: 0.7rem;
  letter-spacing: 0.1em;
  font-weight: 600;
  color: var(--brand-text-muted);
  margin-bottom: 0.5rem;
  text-transform: lowercase;
`;

const HeroTitle = styled.h1`
  font-family: 'Space Grotesk', sans-serif;
  font-size: clamp(2rem, 5vw, 3.2rem);
  font-weight: 400;
  letter-spacing: -0.03em;
  color: var(--brand-text);
  line-height: 1.15;
  margin-bottom: 1rem;
`;

const HeroSub = styled.p`
  font-size: clamp(1rem, 2.5vw, 1.1rem);
  color: #666;
  line-height: 1.6;
  margin: 0 0 2rem;
`;

/**
 * Was a <blockquote> carrying an unattributed testimonial about recovering a
 * security deposit "in a week" (removed 2026-09-11). Nobody was attached to
 * it, nothing substantiated it, and it described a LEGAL OUTCOME to
 * consumers — the shape 16 CFR 465 and the FTC's endorsement rules reach
 * hardest, and the shape FTC v. DoNotPay was about.
 *
 * No longer a blockquote: what stands here is our own description of what the
 * product does, and our own words inside quotation marks would be the
 * misrepresentation in miniature. A real consented quote can restore it, with
 * its sourcing recorded in config/marketing-claims.json — and even then it
 * must not promise an outcome.
 */
const HeroNote = styled.p`
  font-size: clamp(0.9rem, 2.5vw, 1rem);
  color: #555;
  border-left: 2px solid var(--brand-gold);
  padding-left: 1rem;
  margin-bottom: 1.25rem;
`;

/**
 * The plain line this page did not carry (2026-09-11).
 *
 * /for-individuals offers demand letters, lease analysis and immigration-form
 * help to people who are not lawyers, under the heading "Your Personal Legal
 * Intelligence", and said nowhere that it is not a law firm. Disclaimers
 * existed on generated documents; the page selling the service had none.
 * FTC v. DoNotPay ($193k, February 2025) is the same fact pattern, and
 * California's Immigration Consultant Act (B&P §22440) treats the
 * immigration half as its own category.
 *
 * Deliberately quiet and matter-of-fact rather than a warning box: it belongs
 * where a reader is deciding, and a wall of red would be both worse to read
 * and no more protective.
 */
const Disclaimer = styled.p`
  font-size: 0.82rem;
  line-height: 1.6;
  color: #6b6b6b;
  margin: 0.75rem 0 0;
`;

/** The terms under the button, read from the SSOT rather than typed. */
const TrialNote = styled.p`
  font-size: 0.8rem;
  line-height: 1.55;
  color: var(--brand-text-muted);
  margin: 0.7rem 0 0;
`;

const RightColumn = styled.div`
  animation: ${fadeIn} 0.55s 0.1s ease both;
  background: #FFF;
  border-radius: 1.5rem;
  border: 1.5px solid var(--brand-text);
  box-shadow: 0 1rem 3rem rgba(0, 0, 0, 0.1);
  padding: 3rem 2rem;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
`;

const ActionButton = styled.button`
  background: var(--brand-text);
  color: #fff;
  border: none;
  border-radius: 0.85rem;
  padding: 1rem 2rem;
  font-size: 1rem;
  font-weight: 500;
  font-family: inherit;
  cursor: pointer;
  width: 100%;
  margin-top: 1.5rem;
  transition: background 0.15s ease;

  &:hover { background: #2a2a2a; }
`;

const ForIndividuals: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const handleAction = () => {
    if (isAuthenticated) {
      navigate('/');
    } else {
      navigate('/auth');
    }
  };

  return (
    <Page>
      <SeoHead
        title="Legal Demystified with AI | prest0"
        description="Navigate the legal system with confidence. Understand contracts, draft demand letters, and analyze court documents with our consumer legal AI."
        keywords="free legal AI, AI legal help, understand contracts AI, write demand letter AI, self-help legal AI, tenant rights AI"
        canonicalUrl="https://prest0.ai/for-individuals"
      />


      <TwoColumnLayout>
        <div>
          <Hero>
            <Eyebrow>Empower Yourself</Eyebrow>
            <HeroTitle>Your Personal Legal Intelligence</HeroTitle>
            <HeroSub>Don't sign without understanding. Let prest0 analyze leases, draft simple demand letters, answer procedural courtroom questions, and help you navigate immigration forms.</HeroSub>
            <HeroNote>
              Read a lease before you sign it, understand what a notice actually says,
              draft a straightforward demand letter, and work out which immigration form
              applies — in plain language, at your own pace.
            </HeroNote>
            <Disclaimer>
              prest0 is not a law firm and does not provide legal advice. What it produces
              is information and drafting help, not a substitute for a lawyer — and for
              anything with a deadline, a court date or an immigration filing, have a
              licensed attorney review it before you rely on it.
            </Disclaimer>
          </Hero>
        </div>

        <RightColumn>
          <img src="/images/prest0-logo.webp" alt="prest0 logo" width="80" style={{marginBottom: '2rem'}} />
          <h2 style={{fontSize: '1.5rem', marginBottom: '1rem', color: 'var(--brand-text)', fontFamily: 'Space Grotesk'}}>Ready to get started?</h2>
          <p style={{color: '#666', marginBottom: '1rem', fontSize: '0.95rem'}}>
            Experience how prest0 is changing the legal landscape for you.
          </p>
          {/* Was "Start Using Free Now" (2026-09-11), which implies a free tier
              that does not exist. There is a trial: no card, a real credit, a
              clock. Both numbers come from the SSOT so the button cannot drift
              from what the wallet actually loads. */}
          <ActionButton onClick={handleAction}>
            Start free — no card needed
          </ActionButton>
          <TrialNote>
            {formatTrialCreditUsd()} of credit, {ORGANIC_TRIAL_DAYS} days, and no card.
            it does not turn into a subscription on its own.
          </TrialNote>
        </RightColumn>
      </TwoColumnLayout>

    </Page>
  );
};

export default ForIndividuals;
